import { useEffect, useRef } from 'react';
import { Message } from './Message';
import type { ChatMessage } from '../../types';
import { Loader2 } from 'lucide-react';

interface MessageListProps {
  messages: ChatMessage[];
  isLoading?: boolean;
}

export function MessageList({ messages, isLoading }: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const lastAssistantMessage = [...messages].reverse().find((message) => message.role === 'assistant');
  const assistantHasContent = Boolean(
    lastAssistantMessage &&
      ((lastAssistantMessage.content && lastAssistantMessage.content.trim()) ||
        (lastAssistantMessage.contentBlocks &&
          lastAssistantMessage.contentBlocks.some((block) =>
            block.type === 'text' ? block.content.trim() : true
          )) ||
        (lastAssistantMessage.thinkingContent && lastAssistantMessage.thinkingContent.trim()))
  );
  const showThinking = Boolean(isLoading && !assistantHasContent);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isAtBottom = distanceFromBottom <= 20;
      const wasScrollingUp = scrollTop < lastScrollTopRef.current - 1;

      if (wasScrollingUp) {
        shouldAutoScrollRef.current = false;
      } else if (isAtBottom) {
        shouldAutoScrollRef.current = true;
      }
      lastScrollTopRef.current = scrollTop;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (shouldAutoScrollRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center p-4 sm:p-8 text-center">
        <div className="max-w-2xl space-y-4 sm:space-y-6">
          <div className="space-y-2 sm:space-y-3">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary via-purple-500 to-pink-500 bg-clip-text text-transparent animate-gradient">
              Welcome to Nebula
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-muted-foreground/90 max-w-lg mx-auto leading-relaxed px-2">
              Your AI-powered music analytics assistant. Explore artist metrics,
              playlist performance, audience demographics, and industry trends.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={scrollContainerRef} className="h-full overflow-y-auto">
      <div className="flex flex-col mx-auto max-w-3xl">
        {messages.map((message) => (
          <Message key={message.id} message={message} />
        ))}
        {showThinking && (
          <div className="flex items-center gap-2 px-6 py-4 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Thinking...</span>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
